// Consumer Dashboard page

// Default
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
// Mui
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import {
  Card,
  CardContent,
  Checkbox,
  Chip,
  Grid2,
  IconButton,
  Stack,
  Switch,
} from "@mui/material";

// Custom
import Layout from "../../layout";
import Spinner from "../../components/spinner/Spinner";
import ConsumerHeader from "../../components/consumerCardsMoc/ConsumerHeader";
import CustomToast from "../../components/toast/CustomToast";
import ConfirmationModal from "../../components/modals/ConfirmationModal";

// SVG
import Home from "../../assets/svg/home";
import Download from "../../assets/svg/download";
import Icon360 from "../../assets/svg/360";

// Theme
import theme from "../../theme";

// Redux
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  setAllMeters,
  setMeterId,
  setTotalMeters,
} from "../../redux/features/MeterSlice";
import {
  setCustomerDetails,
  setCustomerId,
  setHasSearched,
  setIsCustomerLoading,
} from "../../redux/features/CustomerSlice";

// Utils
import { fetchGETRequest, fetchPUTRequest } from "../../utils/services";
import {
  formatDate,
  formatMeterSerialNumber,
  formatTime,
  isUUID,
} from "../../utils/helper";
import {
  BillingDeterminationItem,
  ToastState,
  TransactionItem,
} from "../../types";
import CustomerMock from "../../components/consumerCardsMoc/Customer";
import CustomDataTable from "../../components/table/CustomTable";
import moment from "moment-timezone";
import Calendar2 from "../../assets/svg/calendar2";
import ContainedButton from "../../forms/button/ContainedButton";
import DateAndTimePopover from "../../components/popovers/DateAndTimePopover";
import { FileChartColumnIncreasing, Gauge, RefreshCw } from "lucide-react";
import CustomDatePicker from "../../components/datePicker/DatePicker";
import CustomInput from "../../forms/input/Input";
import DatePickerInput from "../../forms/input/DatePickerInput";
import {
  setBillingData,
  setBillingDeterminants,
  setSelectedMeter,
  setTransactionsHistory,
} from "../../redux/features/BillingDeterminantSlice";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import Clock from "../../assets/svg/clock";
import Share from "../../assets/svg/share";
import ConsumerMeterDrawerContent from "../../components/consumerCardsMoc/ConsumerMeterDrawerContent";
import ConsumerBillingDrawerContent from "../../components/consumerCardsMoc/ConsumerBillingDrawerContent";
import ViewBillModal from "../../components/modals/ViewBillModal";
import { paginateData } from "../../utils/paginationHelper";

// Types

interface MeterData {
  id: string;
  refId?: string;
  meterNumber: string;
  name?: string | null;
  meterType: string;
  isConnected?: boolean;
  connectionStatus?: string;
  loadStatus?: string;
  operationalStatus?: string;
  isActive?: boolean;
  installationDate?: string | null;
  siteId?: string;
  siteName?: string;
  createdAt?: string;
  updatedAt?: string;
  status?: string | null;
  timeseries: {
    date: string;
    value: string;
  };
}

interface MeterItem extends Omit<MeterData, "timeseries"> {
  lastReadingDate?: string;
  lastReadValue?: string;
  operationalStatus?: string;
  timeseries?: {
    date: string;
    value: string;
  };
}

interface BillingData {
  meterId: string;
  periodStart: number;
  periodEnd: number;
  totalUsage: number;
  amount: number;
  status: string;
  startkWh?: number;
  endkWh?: number;
  tariff?: number;
}

interface ConfirmModalState {
  open: boolean;
  data: any;
  type: string;
}

// Meter columns are now defined inside component with useMemo for select all functionality
// const columns = [
//   "", // Checkbox column
//   "Meter ID",
//   "Meter Name/Sr. No.",
//   "Meter Status",
//   "Disconnect/Connect",
//   "Type",
//   "Last Reading Date",
//   "Last Read Value(kWh)",
//   "Actions",
// ];

const billingColumns = [
  "Billing cycle start",
  "Billing cycle end",
  "Start Read",
  "End Read",
  "Total usage",
  "Tariff",
  "Bill amount",
  "Status",
];

const billingDeteminateColumns = [
  "Start Date",
  "End Date",
  "Min Read",
  "Max Read",
  "Type",
  "Usage",
];

const getTimeStamp = (newTime: string, newDate: Date): number => {
  const [hours, minutes] = newTime.split(":").map(Number);

  // Use moment-timezone to create a moment in IST
  const istMoment = moment.tz(newDate, "Asia/Kolkata").set({
    hour: hours,
    minute: minutes,
    second: 0,
    millisecond: 0,
  });

  return istMoment.valueOf(); // Returns UTC timestamp representing the selection in IST
};

const ConsumerDashboardMoc: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const matches = useMediaQuery(theme.breakpoints.down("lg"));
  const [meterDrawerOpen, setMeterDrawerOpen] = useState<MeterData | null>(
    null,
  );
  const [meterPagination, setMeterPagination] = useState({
    currentPage: 0,
    totalPage: 0,
    totalElement: 0,
    pageSize: 10,
  });
  const [anchorElBilling, setAnchorElBilling] = useState<HTMLElement | null>(
    null,
  );
  const [timeRangeBilling, setTimeRangeBilling] = useState<number[]>([0, 1439]);
  const [billingDrawerOpen, setBillingDrawerOpen] = useState<boolean>(false);
  const [viewBillOpen, setViewBillOpen] = useState<boolean>(false);
  const { hasSearched, isCustomerLoading, customerId } = useAppSelector(
    (state) => state.customer,
  );
  const {
    transactionsHistory,
    billingDeterminants,
    billingData,
    selectedMeter,
  } = useAppSelector((state) => state.billing);

  // const selectedMeter = customerId || location?.state;
  // const [startDate, setStartDate] = useState<Date>(new Date(1751331755000));
  // const [endDate, setEndDate] = useState<Date>(new Date(1754004648000));
  const [billHistoryDate, setBillHistoryDate] = useState({
    startDate: moment().startOf("month").toDate(),
    endDate: moment().endOf("month").toDate(),
  });
  const [billDeterminantionDate, setBillDeterminationDate] = useState({
    startDate: new Date(1751331755000),
    endDate: new Date(1754004648000),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isBillingLoading, setIsBillingLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });
  // const [timeStamp, setTimeStamp] = useState<TimeStampState>({
  //   start: 1751331755000,
  //   end: 1754004648000,
  // });
  // const [timeRange, setTimeRange] = useState<number[]>([0, 1439]);

  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    open: false,
    data: null,
    type: "",
  });
  const [statusLoader, setStatusLoader] = useState(false);
  const [meterSearchText, setMeterSearchText] = useState("");
  const [meterSearchLoading, setMeterSearchLoading] = useState(false);

  // Multi-meter selection state
  const [selectedMeterIds, setSelectedMeterIds] = useState<Set<string>>(
    new Set(),
  );
  const [anchorElMeter, setAnchorElMeter] = useState<HTMLElement | null>(null);
  const [timeRangeMeter, setTimeRangeMeter] = useState<number[]>([0, 1439]);
  const [meterDate, setMeterDate] = useState({
    startDate: moment().startOf("month").toDate(),
    endDate: moment().endOf("month").toDate(),
  });

  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [value, setValue] = useState(searchParams.get("tab") || "meter");

  // Restore state from URL params on mount
  useEffect(() => {
    const view = searchParams.get("view");
    const consumerId = searchParams.get("id");
    const tab = searchParams.get("tab");

    if (tab) {
      setValue(tab);
    }

    // If URL has view=details&id=<consumer_id>, load the consumer details
    if (view === "details" && consumerId) {
      dispatch(setCustomerId(consumerId));
      dispatch(setHasSearched(true));
    }
  }, [searchParams]); // Run when search params change

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("tab", newValue);
      return newParams;
    });
  };

  // const allMeters = useAppSelector((state) => state.meter.allMeters);
  const [allMeters, setAllMetersData] = useState<MeterItem[]>([]);
  const [meterData, setMeterData] = useState<MeterData[]>([]);
  const [billingHistoryData, setBillingHistoryData] = useState<BillingData[]>(
    [],
  );

  // Pagination states for billing table
  const [billingPagination, setBillingPagination] = useState({
    currentPage: 0,
    pageSize: 10,
  });

  useEffect(() => {
    const fetchStatuses = async () => {
      const updatedMeters = await Promise.all(
        allMeters.map(async (meter) => {
          const status = meter.loadStatus || null;
          const timeseries = await getLatestTelemetryV2(meter.meterNumber);
          return { ...meter, status, timeseries };
        }),
      );

      setMeterData(updatedMeters as unknown as MeterData[]);
    };

    if (allMeters.length) {
      fetchStatuses();
    } else {
      setMeterData([]);
    }
  }, [allMeters]);

  console.log("meter data--------", meterData);

  const getLatestTelemetryV2 = async (meterSerial: string) => {
    try {
      const response = await fetchGETRequest<{
        success: boolean;
        data: any;
      }>(`v2/meter-telemetry/instant/latest?meterSerial=${meterSerial}`);

      if (response?.success && response?.data) {
        return {
          date: formatDate(
            new Date(response.data.readingTime),
            "DD MMM YY HH:mm:ss",
            "Asia/Kolkata",
          ),
          value: response.data.cumulativeEnergyKwhImport
            ? Number(response.data.cumulativeEnergyKwhImport).toFixed(4)
            : "0.0000",
        };
      }
      return {
        date: "-",
        value: "-",
      };
    } catch (error: any) {
      console.log(error, "---getLatestTelemetryV2 error---");
      return {
        date: "-",
        value: "-",
      };
    }
  };

  const getTimeSeriesOfMeter = async (deviceID: string) => {
    try {
      const now = Date.now();
      const twentyFourHours = 24 * 60 * 60 * 1000;
      const endTs = now + twentyFourHours;
      const startTs = now - twentyFourHours;
      const response = await fetchGETRequest(
        `plugins/telemetry/DEVICE/${deviceID}/values/timeseries?keys=IP_KWH&startTs=${startTs}&endTs=${endTs}&limit=${10}`,
      );

      if (response) {
        return {
          date:
            (response?.["IP_KWH"]?.[0]?.ts &&
              formatDate(
                new Date(response?.["IP_KWH"]?.[0]?.ts),
                "DD MMM YY HH:mm:ss",
                "Asia/Kolkata",
              )) ||
            "-",
          value:
            Math.round(response["IP_KWH"]?.[0]?.value * 1000) / 1000 || "-",
        };
      } else {
        return {
          date: "-",
          value: "-",
        };
      }
    } catch (error: any) {
      console.log(error, "---getTimeSeriesOfMeter error---");
      return {
        date: "-",
        value: "-",
      };
    }
  };

  const getMeterConnectionStatus = async (deviceId: any) => {
    try {
      const deviceIdStr = typeof deviceId === "object" ? deviceId.id : deviceId;
      const response = await fetchGETRequest(
        `devices/${deviceIdStr}/connectionstatus`,
        {},
        true,
      );

      if (response?.data?.Status) {
        switch (response?.data?.Status?.trim().toUpperCase()) {
          case "DISCONNECT SUCCESS":
            return "Disconnect";
          case "DISCONNECTED":
            return "Disconnected";
          case "DISCONNECT COMPLETED":
            return "Disconnect Completed";
          case "CONNECT SUCCESS":
            return "Connect";
          case "DISCONNECT IN PROGRESS":
            return "Disconnect In Progress";
          case "CONNECT IN PROGRESS":
            return "Connect In Progress";
          case "CONNECTED":
            return "Connected";
          case "ONLINE":
            return "Connected";
          case "OFFLINE":
            return "Disconnected";
          default:
            return null;
        }
      } else {
        return null;
      }
    } catch (error: any) {
      console.log(error, "---getMeterConnectionStatus error---");
      return null;
    }
  };

  const handleRefreshStatus = async (meterId: any) => {
    try {
      const meter = meterData.find((m) => m.id === meterId);
      const meterSerial = meter?.meterNumber || "";

      const [status, timeseries] = await Promise.all([
        getMeterConnectionStatus(meterId),
        getLatestTelemetryV2(meterSerial),
      ]);

      setMeterData((prev) =>
        prev.map((m) =>
          m.id === meterId ? ({ ...m, status, timeseries } as MeterData) : m,
        ),
      );
      setToast({ message: "Meter status refreshed", severity: "success" });
    } catch (error: any) {
      setToast({
        message: "Failed to refresh meter status",
        severity: "error",
      });
    }
  };

  const downloadCSV = (data: any[], meter: string) => {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]).join(",");
    const rows = data.map((row) => Object.values(row).join(",")).join("\n");
    const csvContent = `${headers}\n${rows}`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${window.location.pathname.slice(1)}-${meter}.csv`;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const bulkDownload = async () => {
    setIsLoading(true);

    const metersResponse = await fetchGETRequest(
      `v2/Meters/by-customer/${customerId}`,
      {},
      true,
    );

    const startTs = getTimeStamp(
      formatTime(0),
      new Date(billHistoryDate.startDate),
    );
    const endTs = getTimeStamp(
      formatTime(1439),
      new Date(billHistoryDate.endDate),
    );

    if (metersResponse?.data?.data?.length > 0) {
      const allMeters = metersResponse?.data?.data?.map((item: any) => {
        return { id: item?.id, name: item?.name || item?.meterNumber };
      });

      for (let i = 0; i < allMeters.length; i++) {
        const meter = allMeters[i]["id"];
        const response = await fetchGETRequest(
          `plugins/telemetry/DEVICE/${meter}/values/timeseries?keys=IP_KWH,IP_MeterTime,IP_Current,IP_Voltage,IP_Frequency,IP_NeutralCurrent,IP_PowerFactor,IP_KW,IP_KVA,IP_KWHEXP,IP_KVAH,IP_KVAHEXP,IP_MDKW,IP_MDKVA,IP_MDResetCount,IP_ProgramCount,IP_TamperCount,IP_TotalPowerOnDuration,IP_RelayStatus,IP_LoadLimitValue,EventCode,EventDescription&startTs=${startTs}&endTs=${endTs}&intervalType=WEEK`,
          {},
          true,
        );

        if (response?.data && Object.keys(response?.data).length !== 0) {
          const keys = Object.keys(response?.data);
          const maxRows = Math.max(
            ...Object.values(response?.data).map((arr: any) => arr.length),
          );

          const tableData = Array.from({ length: maxRows }).map(
            (_, rowIndex) => {
              let rowObject: any = {
                meter: allMeters[i]["name"],
                timestamp:
                  formatDate(
                    new Date(response?.data[keys[0]][rowIndex]?.ts),
                    "DD/MM/YYYY HH:mm:ss",
                    "Asia/Kolkata",
                  ) || "N/A",
              };
              keys.forEach((key: string) => {
                rowObject[key.replace("IP_", "")] =
                  response?.data[key][rowIndex]?.value || "N/A";
              });
              return rowObject;
            },
          );
          downloadCSV(tableData, meter);
        } else {
          setToast({
            message: `No data found for meter: ${meter}`,
            severity: "error",
          });
        }
      }
    } else {
      setToast({
        message: `No meters found.`,
        severity: "error",
      });
    }

    setIsLoading(false);
  };

  const toggleStatusChange = async () => {
    try {
      setStatusLoader(true);
      const originalId = confirmModal?.data?.id;
      const refId = confirmModal?.data?.refId;
      const commandId = refId || originalId;

      const deviceIdObj = typeof commandId === "object" ? commandId : commandId;

      let body = {
        deviceIds: [deviceIdObj],
        command:
          confirmModal?.type === "connect"
            ? "ExecuteConnect"
            : "ExecuteDisconnect",
        projectName: "",
      };

      const response = await fetchPUTRequest(`devices/command`, body);
      getMeterConnectionStatus(originalId);

      handleSearch(meterPagination.currentPage, meterPagination.pageSize);
    } catch (error: any) {
      setToast({
        message: error.message,
        severity: "error",
      });
    } finally {
      setStatusLoader(false);
      setConfirmModal({
        open: false,
        data: null,
        type: "",
      });
    }
  };

  const handleSearch = async (
    page: number,
    pageSize: number,
    searchText?: string,
  ) => {
    try {
      setMeterSearchLoading(true);
      const textSearchParam =
        searchText !== undefined ? searchText : meterSearchText;

      const [responseLeft, responseRight] = await Promise.all([
        fetchGETRequest(`v2/Customers/${customerId || location?.state}`),
        fetchGETRequest<{
          success: boolean;
          message: string | null;
          data: any; // Can be array or object with data array
        }>(
          `v2/Meters/by-customer/${customerId || location?.state}?pageSize=${pageSize}&page=${page}${textSearchParam ? `&search=${encodeURIComponent(textSearchParam)}` : ""}`,
        ),
      ]);

      if (responseLeft?.success && responseLeft?.data) {
        dispatch(setCustomerDetails(responseLeft.data));
      } else if (responseLeft && !responseLeft.success && !responseLeft.data) {
        dispatch(setCustomerDetails(responseLeft));
      }

      const meterResponse = responseRight?.data;
      const fullMeterList =
        meterResponse?.data ||
        (Array.isArray(meterResponse) ? meterResponse : []);

      if (fullMeterList?.length > 0) {
        // Local filtering
        const filteredData = textSearchParam
          ? fullMeterList.filter(
              (item: any) =>
                item.meterNumber
                  ?.toLowerCase()
                  .includes(textSearchParam.toLowerCase()) ||
                item.id
                  ?.toLowerCase()
                  .includes(textSearchParam.toLowerCase()) ||
                item.siteName
                  ?.toLowerCase()
                  .includes(textSearchParam.toLowerCase()),
            )
          : fullMeterList;

        // Local pagination
        const totalCount = filteredData.length;
        const totalPages = Math.ceil(totalCount / pageSize);
        const startIndex = page * pageSize;
        const paginatedData = filteredData.slice(
          startIndex,
          startIndex + pageSize,
        );

        setMeterPagination({
          currentPage: page,
          totalPage: totalPages,
          totalElement: totalCount,
          pageSize: pageSize,
        });

        const newData: MeterItem[] = paginatedData.map((payload: any) => {
          return {
            ...payload,
            status: payload.loadStatus || null,
          };
        });

        setAllMetersData(newData);

        if (paginatedData.length > 0) {
          dispatch(setSelectedMeter(paginatedData[0].id));
          const firstId = paginatedData[0].id;
          await getMeterConnectionStatus(firstId);
          await getLatestTelemetryV2(paginatedData[0].meterNumber);
        }
      } else {
        setAllMetersData([]);
        setMeterPagination((old) => ({
          ...old,
          currentPage: 0,
          totalPage: 0,
          totalElement: 0,
        }));
      }

      dispatch(setIsCustomerLoading(false));
      setMeterSearchLoading(false);
    } catch (error: any) {
      dispatch(setIsCustomerLoading(false));
      setMeterSearchLoading(false);
      setToast({ message: error.message, severity: "error" });
      setMeterPagination((old) => ({
        currentPage: 0,
        totalPage: 0,
        totalElement: 0,
        pageSize: old.pageSize,
      }));
      setAllMetersData([]);
    }
  };

  const handleBillingDeterminant = async () => {
    if (!selectedMeter) {
      setToast({
        message: "Please Enter the meter serial No.",
        severity: "error",
      });
      return;
    }

    if (!isUUID(selectedMeter)) {
      setToast({
        message: "Please Enter the valid meter serial No.",
        severity: "error",
      });
      return;
    }
    // const deviceId = "30d33fa0-bb60-11ef-85d5-e5b1b2e5df91"
    const startTs = getTimeStamp(
      formatTime(0),
      billDeterminantionDate.startDate,
    );
    const endTs = getTimeStamp(
      formatTime(1439),
      billDeterminantionDate.endDate,
    );
    const keys = "IP_KWH,EventCode";
    const intervalType = "WEEK";

    const response: any = await fetchGETRequest(
      `plugins/telemetry/DEVICE/${selectedMeter}/getBillingDeterminants?keys=${keys}&startTs=${startTs}&endTs=${endTs}&intervalType=${intervalType}`,
    );

    if (response?.IP_KWH && response?.IP_KWH?.length > 0) {
      const values: number[] =
        response?.IP_KWH?.map((item: { value: string }) =>
          parseFloat(item.value || "0"),
        ) || [];
      const max = Math.max(...values);
      const min = Math.min(...values);
      const totalCost = max - min;

      let penaltyCount = 0;

      const penalty = response?.EventCode?.map((item: { value: string }) => {
        if (parseFloat(item.value) >= 200 && parseFloat(item.value) < 300) {
          return penaltyCount++;
        }
      });

      const newBillingDeterminant: BillingDeterminationItem = {
        id: billingDeterminants?.length + 1,
        fromDate: formatDate(
          billDeterminantionDate.startDate,
          "YYYY-MM-DDTHH:mm",
          "Asia/Kolkata",
        ),
        toDate: formatDate(
          billDeterminantionDate.endDate,
          "YYYY-MM-DDTHH:mm",
          "Asia/Kolkata",
        ),
        timestamp: new Date().toISOString(),
      };

      const newTransaction: TransactionItem = {
        id: transactionsHistory?.length + 1,
        amount: -totalCost,
        timestamp: new Date().toISOString(),
        animate: true,
      };

      dispatch(
        setTransactionsHistory([...transactionsHistory, newTransaction]),
      );

      dispatch(
        setBillingDeterminants([...billingDeterminants, newBillingDeterminant]),
      );

      const newBillingData = {
        id: newBillingDeterminant.id,
        seqNo: billingDeterminants.length + 1,
        registerType: selectedMeter,
        qu: max.toFixed(3),
        activeEnergy: "1.0.1.8.0.255",
        ut: min.toFixed(3),
        qt: totalCost.toFixed(3),
        penaltyCount,
      };

      dispatch(setBillingData([...billingData, newBillingData]));
      // localStorage.setItem(`billingData_${newBillingDeterminant.id}`, JSON.stringify(newBillingData));
      // dispatch(setBalance(newBalance));

      setBillDeterminationDate({
        startDate: new Date(),
        endDate: new Date(),
      });

      // if (newBalance < 20) {
      //   // await createWorkOrder('disconnect');
      //   setToast({ message: "Wallet balance is below 20. Disconnect work order created.", severity: "error" });
      // }
    } else {
      setBillDeterminationDate({
        startDate: new Date(),
        endDate: new Date(),
      });
      setToast({
        message: "No billing data found for the selected time range.",
        severity: "error",
      });
    }
  };

  const fetchBillingData = async () => {
    const startTs = getTimeStamp(
      formatTime(timeRangeBilling[0]),
      new Date(billHistoryDate.startDate),
    );

    const endTs = getTimeStamp(
      formatTime(timeRangeBilling[1]),
      new Date(billHistoryDate.endDate),
    );
    console.log(
      billHistoryDate,
      new Date(billHistoryDate.startDate),
      "---startTs---",
    );

    try {
      setIsBillingLoading(true);
      const response = await fetchGETRequest<{ bills: BillingData[] }>(
        `billing/CUSTOMER/${customerId}/bills?startTs=${startTs}&endTs=${endTs}`,
      );

      setBillingHistoryData(response?.bills || []);
    } catch (error: any) {
      setToast({ message: error.message, severity: "error" });
    } finally {
      setIsBillingLoading(false);
    }
  };

  const handleBillingDateChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setBillHistoryDate({
      startDate: start,
      endDate: end,
    });
  };

  // Meter selection handlers
  const handleSelectMeter = (meterId: string) => {
    setSelectedMeterIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(meterId)) {
        newSet.delete(meterId);
      } else {
        newSet.add(meterId);
      }
      return newSet;
    });
  };

  const handleSelectAllMeters = () => {
    if (selectedMeterIds.size === meterData.length) {
      setSelectedMeterIds(new Set());
    } else {
      setSelectedMeterIds(new Set(meterData.map((m) => m.id)));
    }
  };

  // Meter columns with select all checkbox
  const meterColumns = useMemo(
    () => [
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Checkbox
          size="small"
          checked={
            meterData.length > 0 && selectedMeterIds.size === meterData.length
          }
          indeterminate={
            selectedMeterIds.size > 0 &&
            selectedMeterIds.size < meterData.length
          }
          onChange={handleSelectAllMeters}
          sx={{
            p: 0,
            color: "#1064ED",
            "&.Mui-checked": {
              color: "#1064ED",
            },
            "&.MuiCheckbox-indeterminate": {
              color: "#1064ED",
            },
          }}
        />
      </Box>,
      "Meter ID",
      "Meter Name/Sr. No.",
      "Site Name",
      "Connection Status",
      "Load Status",
      "Operational Status",
      "Disconnect/Connect",
      "Type",
      "Last Reading Date",
      "Last Read Value(kWh)",
      "Actions",
    ],
    [meterData.length, selectedMeterIds.size],
  );

  const handleMeterDateChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setMeterDate({
      startDate: start,
      endDate: end,
    });
  };

  const downloadSelectedMeters = async (meterId?: string) => {
    const meterIdsToDownload = meterId
      ? [meterId]
      : Array.from(selectedMeterIds);

    if (meterIdsToDownload.length === 0) {
      setToast({
        message: "Please select at least one meter",
        severity: "error",
      });
      return;
    }

    setIsLoading(true);
    const startTs = getTimeStamp(
      formatTime(timeRangeMeter[0]),
      new Date(meterDate.startDate),
    );
    const endTs = getTimeStamp(
      formatTime(timeRangeMeter[1]),
      new Date(meterDate.endDate),
    );

    for (const id of meterIdsToDownload) {
      const meterInfo = meterData.find((m) => m.id === id);
      try {
        const fromDate = moment
          .tz(startTs, "Asia/Kolkata")
          .format("YYYY-MM-DDTHH:mm");
        const toDate = moment
          .tz(endTs, "Asia/Kolkata")
          .format("YYYY-MM-DDTHH:mm");

        const response = await fetchGETRequest<any>(
          `v2/meter-reads/browse?type=instant&skip=0&take=10000&search=${meterInfo?.meterNumber || id}&from=${fromDate}&to=${toDate}`,
          {},
          true,
        );

        // response is AxiosResponse because of 'true' argument
        const apiResult = response?.data;
        const records = apiResult?.data?.records || apiResult?.records || [];

        const tableData =
          records.length > 0
            ? records.map((record: any) => {
                const readingTime =
                  record.readingTime || record.receivedDate || record.ts;
                return {
                  meter: formatMeterSerialNumber(meterInfo?.meterNumber) || id,
                  timestamp:
                    formatDate(
                      new Date(readingTime),
                      "DD/MM/YYYY HH:mm:ss",
                      "Asia/Kolkata",
                    ) || "N/A",
                  kWh: record.cumulativeEnergyKwhImport ?? "0.0000",
                  voltage: record.voltage ?? "0.0",
                  phaseCurrent: record.phaseCurrent ?? "0.0",
                  neutralCurrent: record.neutralCurrent ?? "0.0",
                  powerFactor: record.powerFactor ?? "0.0",
                  frequency: record.frequency ?? "0.0",
                  activePowerW: record.activePowerW ?? "0.0",
                  apparentPowerVa: record.apparentPowerVa ?? "0.0",
                };
              })
            : [
                {
                  meter: formatMeterSerialNumber(meterInfo?.meterNumber) || id,
                  timestamp: "N/A",
                  kWh: "No data",
                  voltage: "0.0",
                  phaseCurrent: "0.0",
                  neutralCurrent: "0.0",
                  powerFactor: "0.0",
                  frequency: "0.0",
                  activePowerW: "0.0",
                  apparentPowerVa: "0.0",
                },
              ];

        downloadCSV(
          tableData,
          formatMeterSerialNumber(meterInfo?.meterNumber) || id,
        );

        if (records.length === 0) {
          setToast({
            message: `No data found for meter: ${formatMeterSerialNumber(meterInfo?.meterNumber) || id}`,
            severity: "error",
          });
        }
      } catch (error: any) {
        setToast({
          message: `Error downloading meter ${formatMeterSerialNumber(meterInfo?.meterNumber) || id}: ${error.message}`,
          severity: "error",
        });
      }
    }

    setIsLoading(false);
    setToast({
      message: `Downloaded data for ${meterIdsToDownload.length} meter(s)`,
      severity: "success",
    });
  };

  useEffect(() => {
    if (customerId) {
      fetchBillingData();
      handleSearch(0, meterPagination.pageSize);
    } else {
      setBillingHistoryData([]);
    }
  }, [customerId]);

  const CHIP_COLORS = {
    good: "#34C759",
    pending: "#FDA81F",
    failed: "#F65B5B",
    high: "#F65B5B",
    medium: "#FDA81F",
    low: "#34C759",
    info: "#007AFF",
    closed: "#34C759",
    open: "#F65B5B",
    "investigation open": "#FDA81F",
  };

  const getStatusChip = (type: string) => {
    return (
      <Box
        sx={{
          padding: "1px 8px",
          borderRadius: "100px",
          width: "fit-content",
          background:
            CHIP_COLORS[type.toLowerCase() as keyof typeof CHIP_COLORS],
        }}
      >
        <Typography variant="tableBody" fontWeight={600} color="#ffffff">
          {type}
        </Typography>
      </Box>
    );
  };

  // Paginate billing data
  const paginatedBillingResult = useMemo(() => {
    return paginateData(
      billingHistoryData,
      billingPagination.currentPage,
      billingPagination.pageSize,
    );
  }, [billingHistoryData, billingPagination]);

  const RowContents = meterData.map((row, ind) => (
    <TableRow key={ind}>
      <TableCell sx={{ p: 0.5, width: 50, textAlign: "center" }}>
        <Checkbox
          size="small"
          checked={selectedMeterIds.has(row.id)}
          onChange={() => handleSelectMeter(row.id)}
          onClick={(e) => e.stopPropagation()}
          sx={{
            p: 0,
            color: "#1064ED",
            "&.Mui-checked": {
              color: "#1064ED",
            },
          }}
        />
      </TableCell>
      <TableCell
        sx={{ cursor: "pointer" }}
        onClick={() => {
          setMeterDrawerOpen(row);
        }}
      >
        <Typography variant="tableBody" color="#1064ED" fontWeight={700}>
          {row?.id}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {formatMeterSerialNumber(row?.meterNumber) || "="}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {row?.siteName || "N/A"}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Box>
          {(row.connectionStatus === "Online" ||
            row.connectionStatus === "Connected" ||
            row.connectionStatus === "Connected Success") && (
            <Chip
              sx={{ color: "#fff !important" }}
              label={row.connectionStatus}
              className="consumer_left_card_status_chip consumer_left_card_status_active"
            />
          )}
          {(row.connectionStatus === "Offline" ||
            row.connectionStatus === "Disconnected" ||
            row.connectionStatus === "Disconnected Success") && (
            <Chip
              label={row.connectionStatus}
              className="consumer_left_card_status_chip consumer_left_card_status_disconnected"
            />
          )}
          {!(
            row.connectionStatus === "Online" ||
            row.connectionStatus === "Connected" ||
            row.connectionStatus === "Connected Success" ||
            row.connectionStatus === "Offline" ||
            row.connectionStatus === "Disconnected" ||
            row.connectionStatus === "Disconnected Success"
          ) && (
            <Chip
              label={row.connectionStatus || "Unknown"}
              className="consumer_left_card_status_chip consumer_left_card_status_disconnected"
            />
          )}
        </Box>
      </TableCell>
      <TableCell align="center">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Box>
            {(row.status === "Connect" ||
              row.status === "Online" ||
              row.status === "Connected" ||
              row.status === "Connected Success") && (
              <Chip
                sx={{ color: "#fff !important" }}
                label={row.status}
                className="consumer_left_card_status_chip consumer_left_card_status_active"
              />
            )}
            {row.status == "Connect In Progress" && (
              <Chip
                sx={{ color: "#fff !important" }}
                label={row.status}
                className="consumer_left_card_status_chip consumer_left_card_status_connecting_progress"
              />
            )}
            {(row.status == "Disconnected" ||
              row.status == "Disconnect In Progress" ||
              row.status == "Disconnect Completed" ||
              row.status == "Disconnected Success") && (
              <Chip
                label={row.status}
                className="consumer_left_card_status_chip consumer_left_card_status_disconnected"
              />
            )}
            {(!row.status ||
              row.status == "Unknown" ||
              row.status == "null") && (
              <Chip
                label={row.isActive ? "Connected" : "Disconnected"}
                sx={{
                  color: row.isActive ? "#fff !important" : "inherit",
                }}
                className={`consumer_left_card_status_chip ${
                  row.isActive
                    ? "consumer_left_card_status_active"
                    : "consumer_left_card_status_disconnected"
                }`}
              />
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell align="center">
        <Box>
          {(row.operationalStatus === "Connected" ||
            row.operationalStatus === "Connected Success") && (
            <Chip
              sx={{ color: "#fff !important" }}
              label={row.operationalStatus}
              className="consumer_left_card_status_chip consumer_left_card_status_active"
            />
          )}
          {(row.operationalStatus?.toLowerCase() === "connect in progress" ||
            row.operationalStatus?.toLowerCase() ===
              "disconect in progress") && (
            <Chip
              sx={{ color: "#fff !important" }}
              label={row.operationalStatus}
              className="consumer_left_card_status_chip consumer_left_card_status_connecting_progress"
            />
          )}
          {(row.operationalStatus === "Disconnected" ||
            row.operationalStatus === "Disconnected Success") && (
            <Chip
              label={row.operationalStatus}
              className="consumer_left_card_status_chip consumer_left_card_status_disconnected"
            />
          )}
          {row.operationalStatus &&
            !(
              row.operationalStatus === "Connected" ||
              row.operationalStatus === "Connected Success" ||
              row.operationalStatus === "Disconnected" ||
              row.operationalStatus === "Disconnected Success" ||
              row.operationalStatus?.toLowerCase() === "connect in progress" ||
              row.operationalStatus?.toLowerCase() === "disconect in progress"
            ) && (
              <Chip
                label={row.operationalStatus}
                className="consumer_left_card_status_chip consumer_left_card_status_disconnected"
              />
            )}
          {!row.operationalStatus && "-"}
        </Box>
      </TableCell>
      <TableCell align="center">
        <Switch
          checked={
            row.status === "Connect" ||
            row.status === "Online" ||
            row.status === "Connected" ||
            row.status === "Connected Success" ||
            ((!row.status || row.status === "Unknown") && row.isActive)
          }
          title="Meter Disconnect/Connect"
          disabled={
            row.status == "Connect In Progress" ||
            row.status == "Disconnect In Progress"
              ? true
              : false
          }
          onClick={() => {
            setConfirmModal({
              open: true,
              data: row,
              type: row.isConnected ? "disconnect" : "connect",
            });
          }}
          size="small"
        />
      </TableCell>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {row?.meterType || "N/A"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {row?.timeseries?.date || "N/A"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {row?.timeseries?.value || "0.0000"}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 100 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            sx={{ p: 0.5 }}
            title="Download"
            onClick={() => downloadSelectedMeters(row.id)}
          >
            <Download />
          </IconButton>
          <IconButton
            sx={{ p: 0.5 }}
            title="Meter 360"
            onClick={() => {
              navigate("/meter-dashboard", {
                state: row.id,
              });
              dispatch(setMeterId((row?.id as any)?.id || row?.id));
            }}
          >
            <Icon360 />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  ));

  const billingHistoryRows = paginatedBillingResult.data.map((row, ind) => (
    <TableRow key={ind}>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {formatDate(
            new Date(row.periodStart),
            "DD/MM/YYYY HH:mm",
            "Asia/Kolkata",
          )}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {formatDate(
            new Date(row.periodEnd),
            "DD/MM/YYYY HH:mm",
            "Asia/Kolkata",
          )}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {Number(row.startkWh).toFixed(4)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {Number(row.endkWh).toFixed(4)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {Number(row.totalUsage).toFixed(4)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          Single slab: INR {Number(row.tariff).toFixed(2)} per unit
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="tableBody" fontWeight={600}>
          {Number(row.amount).toFixed(4)}
        </Typography>
      </TableCell>
      <TableCell>{getStatusChip(row.status)}</TableCell>
    </TableRow>
  ));

  const meterDropData = meterData.map((row) => ({
    label: formatMeterSerialNumber(row.meterNumber),
    value: row.id,
  }));

  const billingDeterminatinData = useMemo(() => {
    return billingData.map((item, index) => ({
      ...item,
      fromDate: billingDeterminants[index]?.fromDate,
      toDate: billingDeterminants[index]?.toDate,
    }));
  }, [billingDeterminants, billingData]);

  const billingDeterminationRowa = billingDeterminatinData.map((row, ind) => (
    <TableRow
      key={ind}
      sx={{
        background: (ind + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell>
        {formatDate(new Date(row.fromDate), "DD/MM/YYYY", "Asia/Kolkata")}
      </TableCell>
      <TableCell>
        {formatDate(new Date(row.toDate), "DD/MM/YYYY", "Asia/Kolkata")}
      </TableCell>
      <TableCell>{row.ut}</TableCell>
      <TableCell>{row.qu}</TableCell>
      <TableCell>IP_KWH</TableCell>
      <TableCell>{row.qt}</TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <ConsumerHeader />
      <CustomToast
        open={toast?.message}
        severity={toast?.severity}
        setToast={setToast}
      />
      {isCustomerLoading && (
        <Box mt={"20px"}>
          <Spinner />
        </Box>
      )}

      {!hasSearched && !isCustomerLoading && (
        <Box display={"flex"} flexDirection={"column"} gap={"20px"}>
          {/* <HeaderAssociatedMeterLogs /> */}
          <CustomerMock />
          {/* <Typography
            variant="h6"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >

          </Typography> */}
        </Box>
      )}

      {hasSearched && !isCustomerLoading && (
        <Box
          sx={{
            width: "100%",
            typography: "body1",
            backgroundColor: "white",
            p: "12px 12px 0px 12px",
            borderRadius: 3,
            mt: 2,
            borderColor: "#E0E0E0",
            borderWidth: "1px",
            overflow: "hidden",
            mb: 2,
          }}
        >
          <TabContext value={value}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList
                onChange={handleChange}
                aria-label="lab API tabs example"
                sx={{ height: 30, minHeight: 30 }}
              >
                {/* <Tab label="Billing" value="billing" /> */}
                <Tab label="Meters" value="meter" />
              </TabList>
            </Box>

            <TabPanel value="meter" sx={{ p: 0 }}>
              <Stack
                flexDirection={"row"}
                alignItems={"center"}
                gap={"10px"}
                justifyContent={"space-between"}
                py={3}
                borderBottom={"1px solid #E0E0E0"}
              >
                <Typography fontSize={"18px"} fontWeight={"500"}>
                  Meter Details
                </Typography>
                <Stack flexDirection={"row"} gap={2} alignItems={"center"}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #E0E0E0",
                      borderRadius: "24px",
                      height: "36px",
                      backgroundColor: "#F5F5F5",
                      overflow: "hidden",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Search meters..."
                      value={meterSearchText}
                      onChange={(e) => setMeterSearchText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setMeterPagination((old) => ({
                            ...old,
                            currentPage: 0,
                          }));
                          handleSearch(
                            0,
                            meterPagination.pageSize,
                            meterSearchText,
                          );
                        }
                      }}
                      style={{
                        border: "none",
                        outline: "none",
                        padding: "10px 14px",
                        fontSize: "14px",
                        fontFamily: "Figtree",
                        width: "280px",
                        backgroundColor: "transparent",
                        color: "#333",
                      }}
                    />
                    <IconButton
                      onClick={() => {
                        setMeterPagination((old) => ({
                          ...old,
                          currentPage: 0,
                        }));
                        handleSearch(
                          0,
                          meterPagination.pageSize,
                          meterSearchText,
                        );
                      }}
                      disabled={meterSearchLoading}
                      sx={{
                        padding: "8px",
                        marginRight: "4px",
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      {meterSearchLoading ? (
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            border: "2px solid #e0e0e0",
                            borderTop: "2px solid #1976d2",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                            "@keyframes spin": {
                              "0%": { transform: "rotate(0deg)" },
                              "100%": { transform: "rotate(360deg)" },
                            },
                          }}
                        />
                      ) : (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                      )}
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      fontWeight: 600,
                      letterSpacing: "0.32px",
                      padding: "1px 8px",
                      width: "fit-content",
                    }}
                  >
                    <Stack
                      direction={"row"}
                      justifyContent={"center"}
                      gap={"5px"}
                      alignItems={"center"}
                      px={"5px"}
                    >
                      <Typography fontSize={"13px"} fontWeight={500}>
                        {formatDate(
                          meterDate.startDate,
                          "D MMMM YYYY",
                          "Asia/Kolkata",
                        )}{" "}
                        -{" "}
                      </Typography>
                      <Typography fontSize={"13px"} fontWeight={500}>
                        {formatDate(
                          meterDate.endDate,
                          "D MMMM YYYY",
                          "Asia/Kolkata",
                        )}
                      </Typography>
                    </Stack>
                  </Box>
                  <IconButton
                    onClick={(event) => setAnchorElMeter(event.currentTarget)}
                  >
                    <Calendar2 />
                  </IconButton>
                  {/* <ContainedButton
                    label={"Generate Bill"}
                    sx={{
                      width: "fit-content",
                      fontWeight: 500,
                      color: "#ffffff",
                      height: "32px",
                      boxShadow: "0",
                      borderRadius: "8px",
                    }}
                  />
                  <ContainedButton
                    label={"View Bill"}
                    sx={{
                      width: "fit-content",
                      fontWeight: 500,
                      color: "#ffffff",
                      height: "32px",
                      boxShadow: "0",
                      borderRadius: "8px",
                    }}
                    onClick={() => setViewBillOpen(true)}
                  /> */}
                  <IconButton
                    title={
                      selectedMeterIds.size > 0
                        ? `Download ${selectedMeterIds.size} meter(s) data`
                        : "Select meters to download"
                    }
                    onClick={() => downloadSelectedMeters()}
                    disabled={isLoading}
                  >
                    <Download />
                  </IconButton>
                  <IconButton
                    title="Refresh Meter Details"
                    onClick={() =>
                      handleSearch(
                        meterPagination.currentPage,
                        meterPagination.pageSize,
                      )
                    }
                    disabled={meterSearchLoading}
                  >
                    <RefreshCw size={16} />
                  </IconButton>
                </Stack>
              </Stack>
              <CustomDataTable
                rowsContent={RowContents}
                columns={meterColumns}
                isLoading={isLoading}
                Actions={true}
                pagination={true}
                rowsPerPage={meterPagination.pageSize}
                rowsPerPageOptions={[10, 20, 50]}
                page={meterPagination.currentPage}
                totalCount={meterPagination.totalElement}
                onPageChange={(newPage, num) => {
                  handleSearch(num, meterPagination.pageSize);
                }}
                onRowsPerPageChange={(e) => {
                  setMeterPagination((old) => ({
                    ...old,
                    currentPage: 0,
                    pageSize: Number(e.target.value),
                  }));
                  handleSearch(0, Number(e.target.value));
                }}
              />

              <DateAndTimePopover
                anchorEl={anchorElMeter}
                setAnchorEl={setAnchorElMeter}
                startDate={meterDate.startDate}
                endDate={meterDate.endDate}
                handleDateChange={handleMeterDateChange}
                timeRange={timeRangeMeter}
                setTimeRange={(timeRange) =>
                  setTimeRangeMeter(
                    Array.isArray(timeRange)
                      ? timeRange
                      : [timeRange, timeRange],
                  )
                }
                handleDateSubmit={() => {}}
              />
            </TabPanel>
            <TabPanel value="billing" sx={{ p: 0 }}>
              {/* <Card
                sx={{
                  borderWidth: "1px",
                  borderColor: "#E0E0E0",
                  borderRadius: 2,
                  overflow: "hidden",
                  mb: 2,
                }}>
                <CardContent sx={{ p: 0 }}>
                  <Stack
                    flexDirection={"row"}
                    alignItems={"center"}
                    gap={"10px"}
                    mb={"20px"}
                    p={3}
                    borderBottom={"1px solid #E0E0E0"}>
                    <FileChartColumnIncreasing size={20} color="#28A6FF" />
                    <Typography fontSize={"20px"} fontWeight={"500"}>
                      Generate New Bill
                    </Typography>
                  </Stack>
                  <Stack px={2}>
                    <Stack
                      flexDirection={"row"}
                      gap={"20px"}
                      alignItems={"end"}>
                      <DatePickerInput
                        value={billHistoryDate.startDate}
                        showTimeSelect={false}
                        onChange={(e) => {
                          setBillHistoryDate((old) => ({
                            ...old,
                            startDate: e ? e : new Date(),
                          }));
                        }}
                        label="Bill Period Start"
                      />

                      <DatePickerInput
                        value={billHistoryDate.endDate}
                        showTimeSelect={false}
                        onChange={(e) => {
                          setBillHistoryDate((old) => ({
                            ...old,
                            endDate: e ? e : new Date(),
                          }));
                        }}
                        label="Bill Period End"
                      />
                      <ContainedButton
                        disabled={
                          !billHistoryDate.startDate || !billHistoryDate.endDate
                        }
                        label={"Generate Bill"}
                        bgColor="#1976d2" //{"linear-gradient(180deg, #1976d2 0%, #0396FF 100%)"}
                        hoverBgColor="#28A6FF" //{"linear-gradient(180deg, #75D3FB 0%, #28A6FF 100%)"}
                        sx={{
                          color: "#ffffff",
                          width: "30%",
                          boxShadow: "0",
                          height: "36px",
                          borderRadius: "8px",
                        }}
                        onClick={fetchBillingData}
                        loading={isBillingLoading}
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card> */}

              <Stack
                flexDirection={"row"}
                alignItems={"center"}
                gap={"10px"}
                justifyContent={"space-between"}
                py={3}
                borderBottom={"1px solid #E0E0E0"}
              >
                <Typography fontSize={"18px"} fontWeight={"500"}>
                  Bill History
                </Typography>
                <Stack flexDirection={"row"} gap={3} alignItems={"center"}>
                  {/* <Box
                sx={{
                  fontWeight: 600,
                  letterSpacing: "0.32px",
                  padding: "1px 8px",
                  color: "#ffffff",
                  width: "fit-content",
                }}>
                <Stack
                  direction={"row"}
                  justifyContent={"center"}
                  gap={"5px"}
                  alignItems={"center"}
                  px={"5px"}>
                  <Typography>
                    {formatDate(startDate, "D MMMM YYYY", "Asia/Kolkata")} -{" "}
                  </Typography>
                  <Typography>
                    {formatDate(endDate, "D MMMM YYYY", "Asia/Kolkata")}
                  </Typography>
                </Stack>
              </Box> */}
                  <ContainedButton
                    label={"Bill Data"}
                    sx={{
                      width: "fit-content",
                      fontWeight: 500,
                      color: "#ffffff",
                      height: "32px",
                      boxShadow: "0",
                      borderRadius: "8px",
                    }}
                    onClick={() => setBillingDrawerOpen(true)}
                  />
                  <Box
                    sx={{
                      fontWeight: 600,
                      letterSpacing: "0.32px",
                      padding: "1px 8px",
                      color: "#ffffff",
                      width: "fit-content",
                    }}
                  >
                    <Stack
                      direction={"row"}
                      justifyContent={"center"}
                      gap={"5px"}
                      alignItems={"center"}
                      px={"5px"}
                    >
                      <Typography fontSize={"13px"} fontWeight={500}>
                        {formatDate(
                          billHistoryDate.startDate,
                          "D MMMM YYYY",
                          "Asia/Kolkata",
                        )}{" "}
                        -{" "}
                      </Typography>
                      <Typography fontSize={"13px"} fontWeight={500}>
                        {formatDate(
                          billHistoryDate.endDate,
                          "D MMMM YYYY",
                          "Asia/Kolkata",
                        )}
                      </Typography>
                    </Stack>
                  </Box>
                  <IconButton
                    onClick={(event) => setAnchorElBilling(event.currentTarget)}
                  >
                    <Calendar2 />
                  </IconButton>
                  <ContainedButton
                    label={"Generate Bill"}
                    sx={{
                      width: "fit-content",
                      fontWeight: 500,
                      color: "#ffffff",
                      height: "32px",
                      boxShadow: "0",
                      borderRadius: "8px",
                    }}
                    onClick={fetchBillingData}
                  />
                  <ContainedButton
                    label={"View Bill"}
                    sx={{
                      width: "fit-content",
                      fontWeight: 500,
                      color: "#ffffff",
                      height: "32px",
                      boxShadow: "0",
                      borderRadius: "8px",
                    }}
                    onClick={() => setViewBillOpen(true)}
                  />
                  <IconButton>
                    <Download />
                  </IconButton>
                  <IconButton>
                    <Share />
                  </IconButton>
                </Stack>
              </Stack>
              <CustomDataTable
                rowsContent={billingHistoryRows}
                columns={billingColumns}
                isLoading={isBillingLoading}
                pagination={true}
                rowsPerPage={billingPagination.pageSize}
                rowsPerPageOptions={[10, 20, 50]}
                totalCount={paginatedBillingResult.totalCount}
                page={billingPagination.currentPage}
                Actions={true}
                onPageChange={(_, newPage) =>
                  setBillingPagination((prev) => ({
                    ...prev,
                    currentPage: newPage,
                  }))
                }
                onRowsPerPageChange={(event) => {
                  const newPageSize = parseInt(event.target.value, 10);
                  setBillingPagination({
                    currentPage: 0,
                    pageSize: newPageSize,
                  });
                }}
              />

              <DateAndTimePopover
                anchorEl={anchorElBilling}
                setAnchorEl={setAnchorElBilling}
                startDate={billHistoryDate.startDate}
                endDate={billHistoryDate.endDate}
                handleDateChange={handleBillingDateChange}
                timeRange={timeRangeBilling}
                setTimeRange={(timeRange) =>
                  setTimeRangeBilling(
                    Array.isArray(timeRange)
                      ? timeRange
                      : [timeRange, timeRange],
                  )
                }
                handleDateSubmit={fetchBillingData}
              />
            </TabPanel>
          </TabContext>
        </Box>
      )}

      {meterDrawerOpen && (
        <Drawer
          anchor={"right"}
          open={true}
          onClose={() => setMeterDrawerOpen(null)}
          PaperProps={{
            sx: {
              backgroundColor: "#F6F7FB",
              boxShadow: "none",
              width: "80%",
              padding: "0px",
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: 16,
              opacity: 1,
            },
          }}
        >
          <Box>
            <ConsumerMeterDrawerContent
              data={meterDrawerOpen as any}
              onClose={() => {
                setMeterDrawerOpen(null);
              }}
              onRefresh={() => {
                handleSearch(
                  meterPagination.currentPage,
                  meterPagination.pageSize,
                );
              }}
            />
          </Box>
        </Drawer>
      )}

      {billingDrawerOpen && (
        <Drawer
          anchor={"right"}
          open={true}
          onClose={() => setBillingDrawerOpen(false)}
          PaperProps={{
            sx: {
              backgroundColor: "#F6F7FB",
              boxShadow: "none",
              width: "60%",
              padding: "0px",
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: 16,
              opacity: 1,
            },
          }}
        >
          <Box>
            <ConsumerBillingDrawerContent
              onClose={() => {
                setBillingDrawerOpen(false);
              }}
            />
          </Box>
        </Drawer>
      )}
      <ConfirmationModal
        confirmModal={confirmModal?.open}
        onClose={() =>
          setConfirmModal({
            open: false,
            data: null,
            type: "",
          })
        }
        onClick={toggleStatusChange}
        body={"Are you sure want to change meter status."}
        isLoading={statusLoader}
      />
      <ViewBillModal
        open={viewBillOpen}
        onClose={() => setViewBillOpen(false)}
        consumerId={customerId || location?.state}
        billingDates={value === "meter" ? meterDate : billHistoryDate}
      />
    </Layout>
  );
};

export default ConsumerDashboardMoc;
